import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Sparkles, Truck, Phone, Mail, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { resolveProductImage } from "../lib/productImages";
import { useSEO } from "../lib/useSEO";
import { OrganizationSchema } from "../components/SEOSchema";
import { FAQSection } from "../components/FAQSection";
import { ServiceTerms } from "../components/ServiceTerms";
import { CITY_LIST } from "../data/cities";
import { HERO_IMAGE as HERO_IMG, CATEGORY_IMAGES } from "../lib/siteImages";
import catalogNavigation from "../data/catalogNavigation.json";

const CAT_IMAGES = {
  ...CATEGORY_IMAGES,
  restauration: resolveProductImage("/api/static/products/pizza-burrata.png"),
};

const Home = () => {
  const categories = catalogNavigation.categories;
  const [featured, setFeatured] = useState([]);

  useSEO({
    title: "NOCTUA RIVIERA — Livraison de nuit 24h/24 Cannes, Nice, Monaco",
    description: "Épicerie livrée 24h/24 de Cannes à Monaco. Restauration de 11h à 4h. Minimum 30 €, livraison 15 € fixes. Espèces, carte ou lien de paiement.",
    path: "/",
  });

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 8)));
  }, []);

  return (
    <div data-testid="home-page">
      <OrganizationSchema />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden noise-overlay">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Livraison de nuit Cannes Monaco — Port de la Riviera française" width="1280" height="896" fetchPriority="high" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a] via-[#0a0f1a]/70 to-[#0a0f1a]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1a] via-transparent to-transparent" />
        </div>

        <div className="relative container-x px-5 sm:px-8 pb-16 sm:pb-24 pt-32 z-10 fade-up">
          <p className="eyebrow mb-5">Épicerie · Riviera</p>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl text-white leading-[0.95] tracking-tight max-w-4xl">
            Votre <span className="shimmer-gold italic">épicerie</span>
            <br />
            ouverte 24h/24.
          </h1>
          <div className="gold-line w-24 my-8" />
          <p className="text-base sm:text-lg text-gray-300 max-w-xl leading-relaxed">
            Boissons, snacks, planches & cigarettes — livrés chez vous, de Cannes à Monaco,
            7 jours sur 7, à toute heure.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-10">
            <Link to="/shop/alcohol" className="btn-gold" data-testid="hero-shop-btn">
              Voir le catalogue
              <ArrowRight size={16} />
            </Link>
            <a href="tel:+33623060308" className="btn-outline-gold" data-testid="hero-call-btn">
              <Phone size={14} />
              06 23 06 03 08
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-12 text-xs uppercase tracking-[0.2em] text-gray-400">
            <span className="flex items-center gap-2"><Clock size={12} className="text-[#d4af37]" /> 24h/24</span>
            <span className="flex items-center gap-2"><Truck size={12} className="text-[#d4af37]" /> Livraison ~30 min</span>
            <span className="flex items-center gap-2"><Sparkles size={12} className="text-[#d4af37]" /> 7 jours sur 7</span>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-white/8 bg-[#0a0f1a]" data-testid="trust-bar">
        <div className="container-x">
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-x divide-white/8">
            {[
              { icon: Clock, k: "24 / 24", v: "Disponible chaque nuit" },
              { icon: Truck, k: "~30 min", v: "Livraison rapide moyenne" },
              { icon: Sparkles, k: "7 j / 7", v: "Y compris jours fériés" },
              { icon: MessageCircle, k: "FR · EN", v: "Équipe bilingue" },
            ].map((t, i) => (
              <li key={i} className={`flex items-center gap-4 px-4 sm:px-6 py-6 ${i >= 2 ? "border-t md:border-t-0 border-white/8" : ""}`} data-testid={`trust-pillar-${i}`}>
                <t.icon size={22} className="text-[#d4af37] shrink-0" strokeWidth={1.4} />
                <div className="min-w-0">
                  <p className="font-display text-2xl text-white leading-none">{t.k}</p>
                  <p className="text-xs uppercase tracking-[0.18em] text-gray-500 mt-2">{t.v}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="section bg-night" data-testid="categories-section">
        <div className="container-x">
          <div className="flex items-end justify-between mb-12 gap-4">
            <div>
              <p className="eyebrow mb-3">Catalogue</p>
              <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">
                Toutes nos <span className="italic text-gold">catégories</span>
              </h2>
            </div>
            <div className="hidden md:block gold-line flex-1 max-w-[200px] mb-3" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {categories.map((cat, i) => (
              <Link
                key={cat.slug}
                to={`/shop/${cat.slug}`}
                data-testid={`category-${cat.slug}-link`}
                className="group relative aspect-[4/3] overflow-hidden border border-subtle hover:border-[#d4af37]/50 transition-all duration-500 fade-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <img
                  src={CAT_IMAGES[cat.slug]}
                  alt={cat.name}
                  width="1024"
                  height="1024"
                  loading="lazy"
                  decoding="async"
                  data-testid={`home-category-image-${cat.slug}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a] via-[#0a0f1a]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="eyebrow mb-1">{cat.name_en}</p>
                  <h3 className="font-display text-3xl text-white leading-none">{cat.name}</h3>
                  <p className="text-xs text-gray-400 mt-2">{cat.tagline}</p>
                  <span className="inline-flex items-center gap-2 mt-4 text-[#d4af37] text-xs uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity">
                    Explorer <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="section bg-night-2" data-testid="featured-section">
          <div className="container-x">
            <div className="flex items-end justify-between mb-12 gap-4">
              <div>
                <p className="eyebrow mb-3">Best-sellers</p>
                <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">
                  Les plus <span className="italic text-gold">commandés</span>
                </h2>
              </div>
              <Link to="/shop/alcohol" className="hidden sm:inline-flex items-center gap-2 text-[#d4af37] text-xs uppercase tracking-[0.2em] hover:gap-3 transition-all">
                Tout voir <ArrowRight size={12} />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {featured.map((p, i) => (
                <div key={p.id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="section bg-night" data-testid="how-it-works-section">
        <div className="container-x">
          <p className="eyebrow text-center mb-3">Comment ça marche</p>
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-none text-center mb-16">
            En trois <span className="italic text-gold">étapes</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Choisissez vos produits", d: "Parcourez le catalogue et ajoutez ce que vous voulez au panier." },
              { n: "02", t: "On vous rappelle", d: "Notre équipe confirme votre commande et votre règlement : espèces, carte bancaire ou lien de paiement." },
              { n: "03", t: "Livraison à domicile", d: "De Cannes à Monaco, avec un délai confirmé lors de votre commande." },
            ].map((s) => (
              <div key={s.n} className="border-l-2 border-[#d4af37] pl-6">
                <p className="font-display text-6xl text-gold leading-none">{s.n}</p>
                <h3 className="font-display text-2xl text-white mt-4">{s.t}</h3>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ZONE */}
      <section className="section bg-night-2" data-testid="zone-section">
        <div className="container-x text-center">
          <p className="eyebrow mb-3">Zone de livraison</p>
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-none mb-8">
            De <span className="italic text-gold">Cannes</span> à <span className="italic text-gold">Monaco</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-x-1 gap-y-3 text-sm text-gray-300 max-w-3xl mx-auto">
            {["Cannes", "Antibes", "Juan-les-Pins", "Cagnes-sur-Mer", "Nice", "Villefranche-sur-Mer", "Beaulieu-sur-Mer", "Saint-Jean-Cap-Ferrat", "Èze", "Cap-d'Ail", "Monaco"].map((z, i, arr) => {
              const city = CITY_LIST.find((c) => c.name === z || c.nameFull === z);
              return (
              <React.Fragment key={z}>
                {city ? <Link to={city.path} className="underline underline-offset-4 decoration-[#d4af37]/40 hover:text-[#d4af37] transition-colors" data-testid={`home-city-${city.slug}`}>{z}</Link> : <span>{z}</span>}
                {i < arr.length - 1 && <span className="divider-dot" />}
              </React.Fragment>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-8 uppercase tracking-[0.2em]">
            Hors zone ? Contactez-nous, on regarde ensemble.
          </p>
          <ServiceTerms testId="home-service-terms" className="mt-6" />
          <Link to="/livraison-alcool-24h24" className="inline-block mt-6 text-sm text-gold underline underline-offset-4 hover:text-white transition-colors" data-testid="home-alcohol-day-night-link">Livraison d’alcool aussi en journée : nos dix villes →</Link>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section" data-testid="contact-section" id="contact">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="eyebrow mb-3">Contact</p>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-none mb-6">
              On est <span className="italic text-gold">là</span>, 24/7
            </h2>
            <p className="text-base text-gray-300 leading-relaxed">
              Une question, une commande, une demande spéciale ? Notre équipe répond à toute heure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            <a
              href="tel:+33623060308"
              className="group bg-night-2 border border-subtle p-8 hover:border-[#d4af37] transition-colors flex flex-col items-center text-center"
              data-testid="contact-phone-card"
            >
              <Phone size={28} className="text-[#d4af37] mb-4" />
              <p className="eyebrow mb-2">Téléphone</p>
              <p className="font-display text-2xl text-white group-hover:text-[#d4af37] transition-colors">
                06 23 06 03 08
              </p>
            </a>

            <a
              href="mailto:noctua.riviera@gmail.com"
              className="group bg-night-2 border border-subtle p-8 hover:border-[#d4af37] transition-colors flex flex-col items-center text-center"
              data-testid="contact-email-card"
            >
              <Mail size={28} className="text-[#d4af37] mb-4" />
              <p className="eyebrow mb-2">Email</p>
              <p className="font-display text-base sm:text-lg text-white group-hover:text-[#d4af37] transition-colors break-all">
                noctua.riviera@gmail.com
              </p>
            </a>

            <a
              href="https://wa.me/33623060308"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-night-2 border border-subtle p-8 hover:border-[#d4af37] transition-colors flex flex-col items-center text-center"
              data-testid="contact-whatsapp-card"
            >
              <MessageCircle size={28} className="text-[#d4af37] mb-4" />
              <p className="eyebrow mb-2">WhatsApp</p>
              <p className="font-display text-2xl text-white group-hover:text-[#d4af37] transition-colors">
                Message direct
              </p>
            </a>
          </div>
        </div>
      </section>

      <FAQSection />
    </div>
  );
};

export default Home;
